import { hexToRgba } from '@e3dc-react/theme'
import { Box, Typography } from '@mui/material'
import { isUndefined } from 'lodash'
import React from 'react'
import { makeStyles } from 'tss-react/mui'

export type LegendItemProps = {
  type: 'bar' | 'line' | 'gradient' | 'donut' | 'rectangle'
  title: string
  color: string
  value?: string
  isDisabled?: boolean
  onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
  dataQa?: string
}

type StyleProps = Pick<LegendItemProps, 'type' | 'color' | 'isDisabled'> & { isClickable?: boolean }

const useStyles = makeStyles<StyleProps>()((theme, { type, color, isDisabled, isClickable }) => ({
  mainWrapper: {
    cursor: isClickable ? 'pointer' : 'default',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(1),
    overflow: 'hidden',
  },
  iconStyle: {
    overflow: 'hidden',
    position: 'relative',
    width: '18px',
    height: '18px',
    borderRadius: '3px',
    flexShrink: 0,
    backgroundColor: theme.palette.grey.grey11,

    // * The rectangle styles (default is bar)
    '&::after': {
      position: 'absolute',
      content: '""',
      height: '100%',
      width: '6px',
      backgroundColor: color,
      top: '0px',
      left: '50%',
      transform: 'translateX(-50%)',

      ...(type === 'line' && {
        transform: 'translateX(-50%) rotate(90deg)',
      }),

      ...(type === 'gradient' && {
        width: '100%',
        height: '100%',
        background: `linear-gradient(360deg, ${hexToRgba(color, 0.2)} 7%, ${hexToRgba(color, 0.6)} 70%, ${color} 90%)`,

        ...(isDisabled && {
          background: `linear-gradient(360deg, ${hexToRgba(theme.palette.grey.grey03, 0.2)} 7%, ${hexToRgba(theme.palette.grey.grey03, 0.5)} 50%, ${
            theme.palette.grey.grey03
          } 90%)`,
        }),
      }),

      ...(type === 'donut' && {
        width: '80%',
        height: '80%',
        borderRadius: '100%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        border: `3.5px solid ${color}`,
        backgroundColor: 'transparent',

        ...(isDisabled && {
          borderColor: theme.palette.grey.grey03,
        }),
      }),

      ...(type === 'rectangle' && {
        width: '100%',
        height: '100%',
        background: color,
        ...(isDisabled && {
          background: theme.palette.grey.grey03,
        }),
      }),

      ...(isDisabled &&
        type !== 'donut' && {
          backgroundColor: theme.palette.grey.grey03,
        }),
    },

    ...(isDisabled && {
      '&::before': {
        position: 'absolute',
        content: '""',
        height: '140%',
        width: '3px',
        backgroundColor: theme.palette.grey.grey11,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%) rotate(45deg)',
        zIndex: theme.zIndex.tooltip,
      },
    }),
  },
  textStyle: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    width: '100%',
    minWidth: 0,
    ...(type === 'donut' && {
      [theme.breakpoints.up('sm')]: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing(1),
      },
      flexWrap: 'nowrap',
    }),
  },
}))

/**
 * A reusable legend item for charts.
 */
const LegendItem: React.FC<LegendItemProps> = ({ type, title, color, value, isDisabled, onClick, dataQa }) => {
  const { classes } = useStyles({ type, color, isDisabled, isClickable: !isUndefined(onClick) })

  return (
    <Box className={classes.mainWrapper} onClick={onClick} data-qa={dataQa}>
      <Box component="i" className={classes.iconStyle} />

      <Box className={classes.textStyle} id="LegendItemText">
        <Typography variant="subtitle2" sx={{ minWidth: 0, flexShrink: 1, flexGrow: 1 }}>
          {title}
        </Typography>
        {value && !isDisabled && (
          <Typography variant="subtitle1" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
            {value}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

export default LegendItem
